import React from 'react'
import type { FunctionComponent, ReactNode } from 'react'
import { Modal, Heading, Text, Button, ModalProps, Divider } from '@afterpaytouch/core'
import { useConsumerSizes } from '../../../utils/responsive'
import styles from './style.module.css'
import { CreditCard } from '@afterpaytouch/portal-api/consumer/account/creditcards/types'
import { PaymentMethodRow } from '../../PaymentMethodRow'
import { BankAccountDetails, CardBrand, CashAppPayDetail, isBankAccount, isCard, isCashAppPay } from '@afterpay/types'
import { maskCreditCardNumber } from '../../../utils/card'
import { PaymentMethodType } from '@afterpaytouch/portal-api/types'
import { useTranslation } from 'next-i18next'

const I18N_NAMESPACE = ['common']
interface ActionModalProps {
  handleClick: () => void
  title: string
  content: ReactNode
  ctaLabel: string
  selectedMethod?: CreditCard | BankAccountDetails | CashAppPayDetail | PaymentMethodType.APPLE_PAY | null
}

const ActionModal: FunctionComponent<ActionModalProps & Omit<ModalProps, 'children'>> = ({
  handleClick,
  onClose,
  onBackdropClick,
  onEscapeKeyDown,
  show,
  title,
  content,
  ctaLabel,
  selectedMethod,
}) => {
  const { sectionHeadingSize, textSize } = useConsumerSizes()
  const { t } = useTranslation(I18N_NAMESPACE)

  const isSelectedMethodCard = selectedMethod !== null && isCard(selectedMethod)
  const isSelectedMethodBank = selectedMethod !== null && isBankAccount(selectedMethod)
  const isSelectedMethodCashApp = selectedMethod !== null && isCashAppPay(selectedMethod)
  const isApplePaySelected = selectedMethod === PaymentMethodType.APPLE_PAY

  const testNameSpace = ctaLabel.toLocaleLowerCase().split(' ').join('-')
  return (
    <Modal onClose={onClose} onBackdropClick={onBackdropClick} onEscapeKeyDown={onEscapeKeyDown} show={show}>
      <Modal.Header>
        <div className={styles.textCenter}>
          <Heading size={sectionHeadingSize}>{title}</Heading>
        </div>
      </Modal.Header>

      <Divider kind={'Hairline'} />
      <Modal.Content>
        {isSelectedMethodCard && (
          <div className={styles.mb8}>
            <PaymentMethodRow
              key={selectedMethod?.id}
              number={maskCreditCardNumber(selectedMethod?.truncatedNumber ?? selectedMethod?.maskedPan)}
              expiryDate={selectedMethod?.expiryDate}
              cardType={CardBrand[selectedMethod?.cardBrand]}
            />
          </div>
        )}
        {isSelectedMethodBank && (
          <div className={styles.mb8}>
            <PaymentMethodRow
              key={selectedMethod?.id}
              type={PaymentMethodType.ACH_BANK_ACCOUNT}
              bankName={selectedMethod?.issuingBank}
              number={maskCreditCardNumber(selectedMethod?.numberMask)}
            />
          </div>
        )}
        {isApplePaySelected && (
          <div className={styles.mb8}>
            <PaymentMethodRow
              isPreferred={false}
              type={PaymentMethodType.APPLE_PAY}
              bodyComponent={
                <div className='flex flex-row items-center'>
                  <div className='ml-2 md:ml-4'>
                    <Text size={textSize}>Apple Pay</Text>
                  </div>
                </div>
              }
            />
          </div>
        )}
        {isSelectedMethodCashApp && (
          <div className={styles.mb8}>
            <PaymentMethodRow type={PaymentMethodType.CASH_APP_PAY} bankName={t('common:cashAppPay')} number={selectedMethod.cashtag} />
          </div>
        )}
        <div className={styles.mb8}>
          <Text size={textSize}>{content}</Text>
        </div>

        <div className={styles.mt16}>
          <Button.Primary padding='Fluid' onClick={handleClick} testNameSpace={testNameSpace}>
            {ctaLabel}
          </Button.Primary>
        </div>
      </Modal.Content>
    </Modal>
  )
}

export default ActionModal
